import { useState, type FormEvent } from 'react';
import { useUser } from '@clerk/react';
import { Trash2 } from 'lucide-react';
import { useDocumentTitle } from '@/shared/hooks';
import { useToast } from '@/shared/contexts/ToastContext';
import { ACCOUNT_ACCENT } from './accountStyles';

type InviteRow = { name: string; email: string };

type TeamMember = {
  id: string;
  name: string;
  email: string;
  addedAt: string;
};

const emptyRow = (): InviteRow => ({ name: '', email: '' });

const initialMembers: TeamMember[] = [
  {
    id: 'tm_1',
    name: 'Jordan Smith',
    email: 'jordan.smith@example.com',
    addedAt: '2026-04-20'
  },
  {
    id: 'tm_2',
    name: 'Riley Jones',
    email: 'riley.jones@example.com',
    addedAt: '2026-05-01'
  }
];

const AccountTeamPage = () => {
  useDocumentTitle('Team - BrokerMax');
  const { notify } = useToast();
  const { user } = useUser();
  const name =
    user?.firstName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    'Agent';

  const [inviteRows, setInviteRows] = useState<InviteRow[]>(() =>
    Array.from({ length: 5 }, emptyRow)
  );
  const [members, setMembers] = useState<TeamMember[]>(initialMembers);

  const updateInviteRow = (
    index: number,
    field: keyof InviteRow,
    value: string
  ) => {
    setInviteRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddBatch = (e: FormEvent) => {
    e.preventDefault();
    const toAdd = inviteRows.filter(
      r => r.email.trim().length > 0 && r.email.includes('@')
    );
    if (toAdd.length === 0) {
      notify('warning', 'Add at least one valid email.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const newMembers: TeamMember[] = toAdd.map((r, i) => ({
      id: `tm_${Date.now()}_${i}`,
      name: r.name.trim() || r.email.trim().split('@')[0],
      email: r.email.trim().toLowerCase(),
      addedAt: today
    }));
    setMembers(prev => [...newMembers, ...prev]);
    setInviteRows(Array.from({ length: 5 }, emptyRow));
    notify('success', `Added ${newMembers.length} team member(s) (demo).`);
  };

  const removeMember = (id: string) => {
    setMembers(prev => prev.filter(m => m.id !== id));
    notify('info', 'Removed from list (demo).');
  };

  return (
    <div className="relative z-10">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        Team
      </h1>
      <p className="mt-1 max-w-xl text-sm text-slate-700">
        Invite agents for {name}. Add up to five people at a time; they&apos;ll
        appear in your roster below.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-2 lg:items-start">
        <form
          onSubmit={handleAddBatch}
          className={`rounded-xl p-5 ${ACCOUNT_ACCENT.cardSurface}`}
        >
          <h2 className="text-sm font-semibold text-slate-900">
            Invite up to 5 at a time
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Name is optional; email is required for each row you want to add.
          </p>
          <div className="mt-4 space-y-3">
            {inviteRows.map((row, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3 sm:flex-row sm:items-end"
              >
                <label className="block flex-1 text-xs font-medium text-slate-600">
                  Name
                  <input
                    type="text"
                    value={row.name}
                    onChange={e =>
                      updateInviteRow(index, 'name', e.target.value)
                    }
                    placeholder="Agent name"
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#2b6cff]/25"
                  />
                </label>
                <label className="block flex-[1.2] text-xs font-medium text-slate-600">
                  Email
                  <input
                    type="email"
                    value={row.email}
                    onChange={e =>
                      updateInviteRow(index, 'email', e.target.value)
                    }
                    placeholder="name@brokerage.com"
                    className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[#2b6cff]/25"
                  />
                </label>
              </div>
            ))}
          </div>
          <button
            type="submit"
            className="mt-5 w-full rounded-lg bg-[#2b6cff] py-2.5 text-sm font-semibold text-white hover:bg-[#2563eb] sm:w-auto sm:px-6"
          >
            Add to team
          </button>
        </form>

        <div
          className={`rounded-xl ${ACCOUNT_ACCENT.cardSurface} overflow-hidden`}
        >
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">
              Team members ({members.length})
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Demo list — wire to Clerk org or your API later.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[400px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/90 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Added</th>
                  <th className="px-4 py-3 font-medium w-14"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800">
                {members.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No team members yet. Use the form to add up to five at a
                      time.
                    </td>
                  </tr>
                ) : (
                  members.map(m => (
                    <tr key={m.id} className="bg-white hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-medium">{m.name}</td>
                      <td className="px-4 py-3 text-slate-600">{m.email}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-500">
                        {m.addedAt}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => removeMember(m.id)}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label={`Remove ${m.name}`}
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountTeamPage;
